/**
 * ZeroSmoke API client – communicates with the FastAPI backend.
 */

const API_BASE = import.meta.env.VITE_API_URL ?? '/api';

// ── Token management ────────────────────────────────────────────
// SSR-safe: never touch localStorage at module init (server has no
// localStorage). First call to getToken()/apiFetch() on the client lazily
// hydrates the cached value; subsequent calls hit the in-memory cache.
const isBrowser = typeof window !== 'undefined';
let accessToken: string | null = null;
let tokenHydrated = false;

function hydrateTokenIfNeeded() {
  if (tokenHydrated || !isBrowser) return;
  accessToken = window.localStorage.getItem('zs_token');
  tokenHydrated = true;
}

export function setToken(token: string | null) {
  accessToken = token;
  tokenHydrated = true;
  if (!isBrowser) return;
  if (token) {
    window.localStorage.setItem('zs_token', token);
  } else {
    window.localStorage.removeItem('zs_token');
  }
}

export function getToken() {
  hydrateTokenIfNeeded();
  return accessToken;
}

// ── Generic fetch wrapper ───────────────────────────────────────
async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> ?? {}),
  };
  hydrateTokenIfNeeded();
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }));
    throw new ApiError(res.status, body.detail ?? 'Request failed');
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

// ── Auth ─────────────────────────────────────────────────────────
export interface AuthToken {
  access_token: string;
  token_type: string;
}

export interface UserOut {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  is_admin: boolean;
  created_at: string;
  /** Count of DELIVERED orders — drives the loyalty badge tier. */
  order_count: number;
  /** Cached spendable ZS Coin balance (1 coin = ₹1). */
  coin_balance: number;
}

export const auth = {
  sendRegisterOtp: (data: {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    phone?: string;
  }) => apiFetch<{ detail: string }>('/auth/send-register-otp', { method: 'POST', body: JSON.stringify(data) }),

  register: (data: {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    phone?: string;
    otp: string;
    referral_code?: string;
  }) => apiFetch<AuthToken>('/auth/register', { method: 'POST', body: JSON.stringify(data) }),

  login: (credentials: { email?: string; phone?: string; password: string }) =>
    apiFetch<AuthToken>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    }),

  me: () => apiFetch<UserOut>('/auth/me'),

  updateProfile: (data: { first_name?: string; last_name?: string; phone?: string }) =>
    apiFetch<UserOut>('/auth/profile', { method: 'PUT', body: JSON.stringify(data) }),

  requestOTP: () =>
    apiFetch<{ detail: string }>('/auth/request-otp', { method: 'POST', body: JSON.stringify({ purpose: 'change_password' }) }),

  changePassword: (otp: string, new_password: string) =>
    apiFetch<{ detail: string }>('/auth/change-password', { method: 'POST', body: JSON.stringify({ otp, new_password }) }),

  forgotPassword: (email: string) =>
    apiFetch<{ detail: string }>('/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) }),

  resetPassword: (email: string, otp: string, new_password: string) =>
    apiFetch<{ detail: string }>('/auth/reset-password', { method: 'POST', body: JSON.stringify({ email, otp, new_password }) }),

  verifyEmail: (email: string, otp: string) =>
    apiFetch<AuthToken>('/auth/verify-email', { method: 'POST', body: JSON.stringify({ email, otp }) }),

  resendVerifyOTP: (email: string) =>
    apiFetch<{ detail: string }>('/auth/resend-verify-otp', { method: 'POST', body: JSON.stringify({ email }) }),
};

// ── Products ─────────────────────────────────────────────────────
export interface PaginatedProducts {
  items: ProductAPI[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

export interface ProductAPI {
  id: string;
  name: string;
  brand: string;
  flavor: string;
  strength: number;
  strength_unit: string;
  price: number;
  original_price: number | null;
  description: string;
  long_description: string | null;
  image: string | null;
  in_stock: boolean;
  featured: boolean;
  best_seller: boolean;
  is_new: boolean;
  rating: number;
  review_count: number;
  pouches_per_can: number;
  format: 'Slim' | 'Mini' | 'Regular';
  category: string;
  color_accent: string;
  stock_quantity: number;
  max_order_qty: number;
  max_order_qty_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProductsQuery {
  page?: number;
  per_page?: number;
  search?: string;
  brand?: string[];
  category?: string[];
  in_stock?: boolean;
  sort?: string;
  min_strength?: number;
  max_strength?: number;
  min_price?: number;
  max_price?: number;
  featured?: boolean;
  best_seller?: boolean;
  is_new?: boolean;
}

function buildQuery(params: ProductsQuery): string {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      value.forEach((v) => sp.append(key, v));
    } else {
      sp.set(key, String(value));
    }
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

export const products = {
  list: (params: ProductsQuery = {}) =>
    apiFetch<PaginatedProducts>(`/products${buildQuery(params)}`),

  get: (id: string) => apiFetch<ProductAPI>(`/products/${id}`),

  notifyWhenAvailable: (productId: string, email: string) =>
    apiFetch<{ detail: string }>(`/products/${productId}/notify`, {
      method: 'POST',
      body: JSON.stringify({ product_id: productId, email }),
    }),
};

// ── Coupons ───────────────────────────────────────────────────────
export interface CouponAPI {
  id: string;
  code: string;
  description: string | null;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  min_order_value: number | null;
  max_discount: number | null;
  usage_limit: number | null;
  used_count: number;
  valid_from: string | null;
  valid_until: string | null;
  is_active: boolean;
  created_at: string;
}

export interface CouponValidateResponse {
  valid: boolean;
  discount_amount: number;
  message: string;
  coupon: CouponAPI | null;
}

export const coupons = {
  active: () => apiFetch<CouponAPI[]>('/coupons/active'),

  validate: (code: string, order_total: number) =>
    apiFetch<CouponValidateResponse>('/coupons/validate', {
      method: 'POST',
      body: JSON.stringify({ code, order_total }),
    }),
};

// ── Wholesale ────────────────────────────────────────────────────
export interface WholesaleCreate {
  name: string;
  business_name: string;
  phone: string;
  email: string;
  city: string;
  products: string;
  quantity: number;
  message: string;
  company_url?: string; // honeypot — real users leave this empty
}

export const wholesale = {
  create: (data: WholesaleCreate) =>
    apiFetch<{ ok: true }>('/wholesale', { method: 'POST', body: JSON.stringify(data) }),
};

// ── Campaigns ─────────────────────────────────────────────────────
export interface ActiveCampaign {
  slug: string;
  name: string;
  offer_text: string;
  cta_label: string;
  cta_link: string;
  starts_at: string | null;
  ends_at: string | null;
}

export const campaigns = {
  active: () => apiFetch<ActiveCampaign | null>('/campaigns/active'),
};

// ── Cart ─────────────────────────────────────────────────────────
export interface CartItemAPI {
  id: string;
  product_id: string;
  quantity: number;
  product: ProductAPI;
}

export const cart = {
  get: () => apiFetch<CartItemAPI[]>('/cart'),

  add: (product_id: string, quantity = 1) =>
    apiFetch<CartItemAPI>('/cart', {
      method: 'POST',
      body: JSON.stringify({ product_id, quantity }),
    }),

  update: (item_id: string, quantity: number) =>
    apiFetch<CartItemAPI>(`/cart/${item_id}`, {
      method: 'PATCH',
      body: JSON.stringify({ quantity }),
    }),

  remove: (item_id: string) =>
    apiFetch<{ detail: string }>(`/cart/${item_id}`, { method: 'DELETE' }),

  clear: () => apiFetch<{ detail: string }>('/cart', { method: 'DELETE' }),
};

// ── Addresses ────────────────────────────────────────────────────
export interface AddressAPI {
  id: string;
  address_line: string;
  city: string;
  state: string;
  pincode: string;
  is_default: boolean;
}

export const addresses = {
  list: () => apiFetch<AddressAPI[]>('/addresses'),

  create: (data: Omit<AddressAPI, 'id'>) =>
    apiFetch<AddressAPI>('/addresses', { method: 'POST', body: JSON.stringify(data) }),

  update: (id: string, data: Omit<AddressAPI, 'id'>) =>
    apiFetch<AddressAPI>(`/addresses/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  setDefault: (id: string) =>
    apiFetch<AddressAPI>(`/addresses/${id}/default`, { method: 'PATCH' }),

  remove: (id: string) =>
    apiFetch<void>(`/addresses/${id}`, { method: 'DELETE' }),
};

// ── Orders ───────────────────────────────────────────────────────
export interface OrderItemAPI {
  id: string;
  product_id: string | null;
  product_name: string;
  unit_price: number;
  quantity: number;
}

export interface OrderAPI {
  id: string;
  status: string;
  total: number;
  shipping: number;
  ship_first_name: string;
  ship_last_name: string;
  ship_email: string;
  ship_phone: string;
  ship_address: string;
  ship_city: string;
  ship_state: string;
  ship_pincode: string;
  coupon_code: string | null;
  discount_amount: number | null;
  coins_redeemed: number | null;
  coin_discount: number | null;
  tracking_id: string | null;
  tracking_url: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  referrer: string | null;
  landing_page: string | null;
  device_type: string | null;
  session_page_views: number | null;
  created_at: string;
  items: OrderItemAPI[];
}

// ── Rewards (ZS Coins + Referral) ────────────────────────────────
export interface CoinTransaction {
  id: string;
  type: 'earn' | 'redeem' | 'referral_referee' | 'referral_referrer' | 'expire' | 'reversal' | 'adjust';
  amount: number;
  status: 'pending' | 'active' | 'expired';
  note: string | null;
  balance_after: number;
  created_at: string;
}
export interface CoinsResponse {
  balance: number;
  pending: number;
  coin_value_inr: number;
  /** Coins expiring in the current calendar month; `on` = ISO month-end date. */
  expiring_this_month: { amount: number; on: string };
  /** Lifetime coins earned (sum of all positive credits). */
  total_earned: number;
  history: CoinTransaction[];
  has_more: boolean;
}
export interface ExpiryItem {
  id: string;
  /** ISO timestamp this lot expires on. */
  expires_at: string;
  /** Unspent coins in this lot. */
  amount: number;
}
export interface ExpiryPage {
  items: ExpiryItem[];
  has_more: boolean;
}
export interface ReferralEntry {
  id: string;
  name: string;
  email: string;
  status: 'pending' | 'qualified' | 'reversed';
  created_at: string;
  qualified_at: string | null;
}
export interface ReferralResponse {
  code: string | null;
  referee_bonus: number;
  referrer_bonus: number;
  stats: { invited: number; qualified: number; coins_earned: number };
  referrals: ReferralEntry[];
  has_more: boolean;
}

export const rewards = {
  coins: (cursor?: string | null) =>
    apiFetch<CoinsResponse>(`/rewards/coins${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`),
  expiry: (cursor?: string | null) =>
    apiFetch<ExpiryPage>(`/rewards/expiry${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`),
  referral: (cursor?: string | null) =>
    apiFetch<ReferralResponse>(`/rewards/referral${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`),
  redeemReferral: (code: string) =>
    apiFetch<{ detail: string; coins: number }>('/rewards/referral/redeem', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
};

export const orders = {
  place: (data: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    pincode: string;
    items: { product_id: string; quantity: number }[];
    coupon_code?: string;
    coins_to_redeem?: number;
    utm_source?: string | null;
    utm_medium?: string | null;
    utm_campaign?: string | null;
    utm_term?: string | null;
    utm_content?: string | null;
    referrer?: string | null;
    landing_page?: string | null;
    device_type?: string | null;
    session_page_views?: number | null;
  }) => apiFetch<OrderAPI>('/orders', { method: 'POST', body: JSON.stringify(data) }),

  list: () => apiFetch<OrderAPI[]>('/orders'),

  get: (id: string) => apiFetch<OrderAPI>(`/orders/${id}`),
};

// ── Reviews ──────────────────────────────────────────────────────
export interface ReviewAPI {
  id: string;
  product_id: string;
  order_id: string | null;
  rating: number;
  text: string;
  is_anonymous: boolean;
  is_featured: boolean;
  display_name: string | null;
  created_at: string;
  user: UserOut;
}

export const reviews = {
  list: (product_id: string) =>
    apiFetch<ReviewAPI[]>(`/reviews?product_id=${encodeURIComponent(product_id)}`),

  featured: (limit = 20) =>
    apiFetch<ReviewAPI[]>(`/reviews/featured?limit=${limit}`),

  create: (data: { product_id: string; rating: number; text: string; order_id?: string; is_anonymous?: boolean }) =>
    apiFetch<ReviewAPI>('/reviews', { method: 'POST', body: JSON.stringify(data) }),

  remove: (id: string) =>
    apiFetch<{ detail: string }>(`/reviews/${id}`, { method: 'DELETE' }),
};
